/**
 * Rating descriptions.
 */
class RatingTaxonomy {
  static VexerGroup() {
    return {
      id: 'vexer',
      type: 'group',
      label: 'Vexers activity',
      items: [
        {
          id: 'vexer:mosquito',
          type: 'rating',
          label: 'Mosquito',
          scale: [
            'No bugs',
            'A few',
            'At witch hour mostly',
            'Quite bad',
            'Swarm',
          ],
          aggregation: 'no',
          imageClass: 'mosquito',
        },
        {
          id: 'vexer:deerfly',
          type: 'rating',
          label: 'Deer/horse flies',
          scale: ['No bugs', 'A few', 'Fair', 'Quite bad', 'Swarm'],
          aggregation: 'no',
          imageClass: 'fly_large',
        },
        {
          id: 'vexer:stablefly',
          type: 'rating',
          label: 'Stable flies',
          scale: ['No bugs', 'A few', 'Fair', 'Quite bad', 'Swarm'],
          aggregation: 'no',
          imageClass: 'fly',
        },
        {
          id: 'vexer:blackfly',
          type: 'rating',
          label: 'Black flies',
          scale: ['No bugs', 'A few', 'Fair', 'Quite bad', 'Swarm'],
          aggregation: 'no',
          imageClass: 'fly_small',
        },
        {
          id: 'vexer:leech',
          type: 'rating',
          label: 'Leeches',
          scale: ['No one', 'A few', 'Some', 'Annoying', 'Swarm'],
          aggregation: 'no',
          imageClass: 'worm',
        },
        {
          id: 'vexer:tick',
          type: 'rating',
          label: 'Ticks',
          scale: ['No one', 'Just one', 'A few', 'Many', 'Dozens'],
          aggregation: 'no',
          imageClass: 'mite',
        },
        {
          id: 'vexer:wasp',
          type: 'rating',
          label: 'Wasps',
          scale: ['No one', 'A few', 'Fair', 'Quite bad', 'Swarm'],
          aggregation: 'no',
          imageClass: 'wasp',
        },
        {
          id: 'vexer:cockroach',
          type: 'rating',
          label: 'Cockroaches',
          scale: ['No one', 'Just one', 'Several', 'A lot', 'Everywhere'],
          aggregation: 'no',
          imageClass: 'cockroach',
        },
        {
          id: 'vexer:other',
          type: 'text',
          label: 'Other',
          aggregation: 'no',
        },
      ],
    };
  }

  static EncounterGroup() {
    return {
      id: 'encounter',
      type: 'group',
      label: 'Encounters',
      items: [
        {
          id: 'encounter:bear',
          type: 'boolean',
          label: 'Bear',
          aggregation: 'last',
          image: '/images/animal_icons.svg#bear_footprint',
        },
        {
          id: 'encounter:racoon',
          type: 'boolean',
          label: 'Racoon',
          aggregation: 'last',
          image: '/images/animal_icons.svg#racoon',
        },
        {
          id: 'encounter:other',
          type: 'text',
          label: 'Other',
          aggregation: 'last',
        },
      ],
    };
  }

  static NeighbourGroup() {
    return {
      id: 'neighbour',
      type: 'group',
      label: 'Neighbours',
      items: [
        {
          id: 'neighbour:bullfrog',
          type: 'boolean',
          label: 'Bullfrog',
          aggregation: 'last',
          image: '/images/animal_icons.svg#bullfrog',
        },
        {
          id: 'neighbour:chipmunk',
          type: 'boolean',
          label: 'Chipmunk',
          aggregation: 'last',
          image: '/images/animal_icons.svg#chipmunk',
        },
        {
          id: 'neighbour:goose',
          type: 'boolean',
          label: 'Goose',
          aggregation: 'last',
          image: '/images/animal_icons.svg#flying_duck',
        },
        {
          id: 'neighbour:snake',
          type: 'boolean',
          label: 'Snake',
          aggregation: 'last',
          image: '/images/animal_icons.svg#snake',
        },
        {
          id: 'neighbour:squirrel',
          type: 'boolean',
          label: 'Squirrel',
          aggregation: 'last',
          image: '/images/animal_icons.svg#squirrel',
        },
        {
          id: 'neighbour:other',
          type: 'text',
          label: 'Other',
          aggregation: 'last',
        },
      ],
    };
  }

  static SleepingGroup() {
    return {
      id: 'sleeping',
      type: 'group',
      label: 'Tent spots',
      items: [
        {
          id: 'sleeping:good-tent-spots',
          type: 'range',
          label: 'Good tent spots',
          min: '0',
          max: '10+',
          aggregation: 'last',
          view: 'none',
        },
        {
          id: 'sleeping:max-tent-spots',
          type: 'range',
          label: 'Max available tent spots',
          min: {
            defaultValue: '0',
            id: 'sleeping:good-tent-spots',
            event: 'input',
            property: 'value',
          },
          max: '10+',
          aggregation: 'last',
          view: {
            label: 'Good / max available tent sites',
            value: `{sleeping:good-tent-spots} / {sleeping:max-tent-spots}`,
          },
        },
        {
          id: 'sleeping:hammock-friendly',
          type: 'rating',
          label: 'Hammock friendly',
          scale: ['No', 'Rather no', 'Could be', 'Yes', 'Definitely'],
          aggregation: 'last',
        },
      ],
    };
  }

  static FeatureGroup(items) {
    return {
      id: 'feature',
      type: 'group',
      label: 'Features',
      dialogLabel: 'Rate feature',
      items,
    };
  }

  static get Size() {
    return {
      id: 'feature:size',
      type: 'rating',
      label: 'Size',
      scale: ['Tiny', 'Small', 'Medium', 'Large', 'Huge'],
      aggregation: 'last',
    };
  }

  static get Cleanliness() {
    return {
      id: 'feature:cleanliness',
      type: 'rating',
      label: 'Cleanliness',
      scale: ['Trashed out', 'Bad', 'Fair', 'Good', 'Very clean'],
      aggregation: 'last',
    };
  }

  static get Firewood() {
    return {
      id: 'feature:firewood',
      type: 'rating',
      label: 'Firewood',
      scale: ['About nothing', 'Low', 'Fair', 'Good', 'Right on campsite'],
      aggregation: 'last',
    };
  }

  static get Novelty() {
    return {
      id: 'feature:novelty',
      type: 'rating',
      label: 'Novelty',
      scale: ['Overused', 'Somewhat tired', 'Fair', 'Good', 'Excellent'],
      aggregation: 'last',
    };
  }

  static get Privacy() {
    return {
      id: 'feature:privacy',
      type: 'rating',
      label: 'Privacy',
      scale: ['No privacy', 'Bad', 'Fair', 'Good', 'Excellent'],
      aggregation: 'last',
    };
  }

  static get Swimming() {
    return {
      id: 'feature:swimming',
      type: 'rating',
      label: 'Swimming',
      scale: ['Next to impossible', 'Questionable', 'Ok', 'Good', 'Excellent'],
      aggregation: 'last',
    };
  }

  static get Wateraccess() {
    return {
      id: 'feature:wateraccess',
      type: 'rating',
      label: 'Water access',
      scale: [
        'Extremely difficult',
        'Difficult',
        'Somewhat difficult',
        'Good',
        'Easy',
      ],
      aggregation: 'last',
    };
  }

  static get View() {
    return {
      id: 'feature:view',
      type: 'rating',
      label: 'View',
      scale: ['Poor', 'Fair', 'Good', 'Picturesque', 'Breathtaking'],
      aggregation: 'last',
    };
  }
}

export { RatingTaxonomy };
